import type { ChangeRequestState } from "@amoreg/contracts";
import type { ThreadSyncPhase } from "../../threadSync";

export type ThreadEndItem =
  | { readonly kind: "background-work"; readonly liveness: "working" | "monitoring" }
  | { readonly kind: "sync"; readonly phase: ThreadSyncPhase }
  | { readonly kind: "woke" }
  | { readonly kind: "parked"; readonly state: "snoozed" | "settled" }
  | { readonly kind: "up-next"; readonly count: number };

export interface ThreadEndInput {
  /** Work that outlives the turn; null while the turn itself is running. */
  readonly backgroundLiveness: "working" | "monitoring" | null;
  /** Null once the thread's messages have loaded, or while its environment is unreachable. */
  readonly syncPhase: ThreadSyncPhase | null;
  readonly woke: boolean;
  readonly snoozed: boolean;
  readonly settled: boolean;
  readonly queuedCount: number;
}

export interface ThreadWokeInput {
  readonly wokeAt: string | null;
  readonly lastVisitedAt: string | undefined;
  readonly latestTurnCompletedAt: string | null | undefined;
  readonly changeRequestState: ChangeRequestState | null | undefined;
}

export function threadWokeVisible(input: ThreadWokeInput): boolean {
  if (input.wokeAt === null) return false;
  if (input.changeRequestState === "merged" || input.changeRequestState === "closed") return false;
  const wokeAtMs = Date.parse(input.wokeAt);
  if (Number.isNaN(wokeAtMs)) return false;
  const storedVisitMs = input.lastVisitedAt ? Date.parse(input.lastVisitedAt) : NaN;
  const completedAtMs = input.latestTurnCompletedAt ? Date.parse(input.latestTurnCompletedAt) : NaN;
  const lastVisitedMs = Math.max(
    Number.isNaN(storedVisitMs) ? -Infinity : storedVisitMs,
    Number.isNaN(completedAtMs) ? -Infinity : completedAtMs,
  );
  return lastVisitedMs < wokeAtMs;
}

export function threadEndState(input: ThreadEndInput): ReadonlyArray<ThreadEndItem> {
  const items: ThreadEndItem[] = [];
  if (input.backgroundLiveness !== null) {
    items.push({ kind: "background-work", liveness: input.backgroundLiveness });
  }
  if (input.syncPhase !== null) items.push({ kind: "sync", phase: input.syncPhase });
  if (input.snoozed) {
    items.push({ kind: "parked", state: "snoozed" });
  } else if (input.woke) {
    items.push({ kind: "woke" });
  } else if (input.settled) {
    items.push({ kind: "parked", state: "settled" });
  }
  if (input.queuedCount > 0) items.push({ kind: "up-next", count: input.queuedCount });
  return items;
}

export function threadEndSummary(items: ReadonlyArray<ThreadEndItem>): string | null {
  const parts = items.flatMap((item) => {
    switch (item.kind) {
      case "background-work":
        return [item.liveness === "working" ? "Working" : "Monitoring"];
      case "up-next":
        return [`${item.count} queued`];
      default:
        return [];
    }
  });
  return parts.length === 0 ? null : parts.join(" · ");
}
